import { useState, useEffect, useCallback } from "react";
import { db } from "../firebase";
import {
  collection,
  onSnapshot,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  query,
  where,
  orderBy,
  serverTimestamp,
} from "firebase/firestore";
import type { FieldValue } from "firebase/firestore";
import { getFunctions, httpsCallable } from "firebase/functions";
import { JOB_ACTIVE, JOB_COMPLETED, JOB_CANCELLED } from "../constants";
import { formatError } from "../utils/formatError";
import { fromDoc } from "../utils/firestoreDoc";
import logger from "../utils/logger";
import type { Job, JobStatus, AttendanceStatus } from "../types";

interface JobFormData {
  title: string;
  entity: string;
  date: string;
  startTime: string;
  endTime: string;
  location: string;
  pointOfContact: string | string[];
  details: string;
  payRate: number | string;
  slotsTotal: number | string;
  isPrivate?: boolean;
  agencyId: string;
}

interface RecurringResult {
  ids: string[];
  errors: { date: string; error: unknown }[];
}

// Idempotency key per call so retried network requests don't double-execute
// the signup/withdraw/waitlist mutation on the server. The server caches the
// result for 24h keyed by (uid, key); without it a flaky network can produce
// "already signed up" errors on what is, from the user's view, the first attempt.
function newIdempotencyKey(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2, 12)}`;
}

export function useJobs(enabled = true, agencyId: string | null = null) {
  const [jobs, setJobs] = useState<Job[]>([]);
  const [loadedFor, setLoadedFor] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const loading = enabled && !!agencyId && loadedFor !== agencyId;

  useEffect(() => {
    if (!enabled || !agencyId) return;
    setError(null);
    // Only load jobs from the last 6 months to prevent unbounded growth
    const cutoff = new Date();
    cutoff.setMonth(cutoff.getMonth() - 6);
    const cutoffStr = cutoff.toISOString().split("T")[0];
    const q = query(
      collection(db, "jobs"),
      where("agencyId", "==", agencyId),
      where("date", ">=", cutoffStr),
      orderBy("date", "asc")
    );
    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        setJobs(snapshot.docs.map((d) => fromDoc<Job>(d)));
        setLoadedFor(agencyId);
      },
      (err) => {
        logger.error("Failed to load jobs:", err);
        setError(formatError(err));
        setLoadedFor(agencyId);
      }
    );
    return () => unsubscribe();
  }, [enabled, agencyId]);

  const createJob = useCallback(async (jobData: JobFormData, userId: string) => {
    try {
      const newJob = {
        ...jobData,
        payRate: Number(jobData.payRate),
        slotsTotal: Number(jobData.slotsTotal),
        signups: [],
        waitlist: [],
        blockedOfficers: [],
        attachments: [],
        attendance: null,
        status: JOB_ACTIVE,
        isPrivate: jobData.isPrivate || false,
        seriesId: null,
        agencyId: jobData.agencyId,
        createdBy: userId,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      };
      const docRef = await addDoc(collection(db, "jobs"), newJob);
      return docRef.id;
    } catch (err) {
      throw new Error(formatError(err));
    }
  }, []);

  const updateJob = useCallback(async (jobId: string, updates: Partial<Job>) => {
    try {
      await updateDoc(doc(db, "jobs", jobId), {
        ...updates,
        updatedAt: serverTimestamp(),
      });
    } catch (err) {
      throw new Error(formatError(err));
    }
  }, []);

  const cancelJob = useCallback(async (jobId: string) => {
    try {
      await updateDoc(doc(db, "jobs", jobId), {
        status: JOB_CANCELLED,
        updatedAt: serverTimestamp(),
      });
    } catch (err) {
      throw new Error(formatError(err));
    }
  }, []);

  const signup = useCallback(async (jobId: string) => {
    try {
      const functions = getFunctions();
      const fn = httpsCallable(functions, "signupForJob");
      await fn({ jobId, idempotencyKey: newIdempotencyKey() });
    } catch (err) {
      throw new Error(formatError(err));
    }
  }, []);

  const withdraw = useCallback(async (jobId: string) => {
    try {
      const functions = getFunctions();
      const fn = httpsCallable(functions, "withdrawFromJob");
      await fn({ jobId, idempotencyKey: newIdempotencyKey() });
    } catch (err) {
      throw new Error(formatError(err));
    }
  }, []);

  const joinWaitlist = useCallback(async (jobId: string) => {
    try {
      const functions = getFunctions();
      const fn = httpsCallable(functions, "joinJobWaitlist");
      await fn({ jobId, idempotencyKey: newIdempotencyKey() });
    } catch (err) {
      throw new Error(formatError(err));
    }
  }, []);

  const leaveWaitlist = useCallback(async (jobId: string) => {
    try {
      const functions = getFunctions();
      const fn = httpsCallable(functions, "leaveJobWaitlist");
      await fn({ jobId, idempotencyKey: newIdempotencyKey() });
    } catch (err) {
      throw new Error(formatError(err));
    }
  }, []);

  const addOfficer = useCallback(async (jobId: string, userId: string) => {
    try {
      const functions = getFunctions();
      const fn = httpsCallable(functions, "assignOfficerToJob");
      await fn({ jobId, userId });
    } catch (err) {
      throw new Error(formatError(err));
    }
  }, []);

  const removeOfficer = useCallback(async (jobId: string, userId: string) => {
    try {
      const functions = getFunctions();
      const fn = httpsCallable(functions, "removeOfficerFromJob");
      await fn({ jobId, userId });
    } catch (err) {
      throw new Error(formatError(err));
    }
  }, []);

  const blockOfficer = useCallback(async (jobId: string, userId: string) => {
    try {
      const functions = getFunctions();
      const fn = httpsCallable(functions, "blockOfficerFromJob");
      await fn({ jobId, userId });
    } catch (err) {
      throw new Error(formatError(err));
    }
  }, []);

  const createRecurringJobs = useCallback(async (jobData: JobFormData, userId: string, dates: string[], seriesId: string): Promise<RecurringResult> => {
    const ids: string[] = [];
    const errors: { date: string; error: unknown }[] = [];
    for (const date of dates) {
      try {
        const newJob = {
          ...jobData,
          date,
          payRate: Number(jobData.payRate),
          slotsTotal: Number(jobData.slotsTotal),
          signups: [],
          waitlist: [],
          blockedOfficers: [],
          attachments: [],
          attendance: null,
          status: JOB_ACTIVE,
          isPrivate: jobData.isPrivate || false,
          seriesId,
          agencyId: jobData.agencyId,
          createdBy: userId,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        };
        const docRef = await addDoc(collection(db, "jobs"), newJob);
        ids.push(docRef.id);
      } catch (err) {
        errors.push({ date, error: err });
      }
    }
    return { ids, errors };
  }, []);

  const deleteJob = useCallback(async (jobId: string) => {
    try {
      await deleteDoc(doc(db, "jobs", jobId));
    } catch (err) {
      throw new Error(formatError(err));
    }
  }, []);

  const sendAnnouncement = useCallback(async (jobId: string, subject: string, message: string, audience: string) => {
    try {
      const functions = getFunctions();
      const fn = httpsCallable(functions, "sendJobAnnouncement");
      const result = await fn({ jobId, subject, message, audience });
      return result.data;
    } catch (err) {
      throw new Error(formatError(err));
    }
  }, []);

  const recordAttendance = useCallback(async (jobId: string, attendanceMap: Record<string, AttendanceStatus>, completeJob = true) => {
    try {
      const update: { attendance: Record<string, AttendanceStatus>; updatedAt: FieldValue; status?: JobStatus } = {
        attendance: attendanceMap,
        updatedAt: serverTimestamp(),
      };
      if (completeJob) {
        update.status = JOB_COMPLETED;
      }
      await updateDoc(doc(db, "jobs", jobId), update);
    } catch (err) {
      throw new Error(formatError(err));
    }
  }, []);

  return {
    jobs,
    loading,
    error,
    createJob,
    createRecurringJobs,
    updateJob,
    cancelJob,
    deleteJob,
    signup,
    withdraw,
    joinWaitlist,
    leaveWaitlist,
    addOfficer,
    removeOfficer,
    blockOfficer,
    sendAnnouncement,
    recordAttendance,
  };
}
